/**
 * Wheel geometry. This is the single source of truth for pocket order — every
 * visual and statistical feature in the app derives from the sequences below.
 * Nothing else in the codebase may define a pocket order.
 */

import type {
  ColumnNumber,
  DozenNumber,
  HighLow,
  Parity,
  Pocket,
  PocketColour,
  PocketId,
  WheelDefinition,
  WheelStyle,
} from './types';

/** Red numbers on a standard felt. Every other non-zero pocket is black. */
export const RED_NUMBERS: ReadonlySet<number> = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

/** Single-zero (European) wheel — physical clockwise order from `'0'`. */
export const SINGLE_ZERO_SEQUENCE: readonly PocketId[] = [
  '0', '32', '15', '19', '4', '21', '2', '25', '17', '34', '6', '27', '13',
  '36', '11', '30', '8', '23', '10', '5', '24', '16', '33', '1', '20', '14',
  '31', '9', '22', '18', '29', '7', '28', '12', '35', '3', '26',
];

/** Double-zero (American) wheel — physical clockwise order from `'0'`. */
export const DOUBLE_ZERO_SEQUENCE: readonly PocketId[] = [
  '0', '28', '9', '26', '30', '11', '7', '20', '32', '17', '5', '22', '34',
  '15', '3', '24', '36', '13', '1', '00', '27', '10', '25', '29', '12', '8',
  '19', '31', '18', '6', '21', '33', '16', '4', '23', '35', '14', '2',
];

function pocketValue(id: PocketId): number | null {
  if (id === '00') return null;
  return Number(id);
}

function colourOf(id: PocketId): PocketColour {
  const value = pocketValue(id);
  if (value === null || value === 0) return 'green';
  return RED_NUMBERS.has(value) ? 'red' : 'black';
}

function buildPocket(id: PocketId, ringIndex: number): Pocket {
  const value = pocketValue(id);
  const isZero = value === null || value === 0;

  let parity: Parity | null = null;
  let highLow: HighLow | null = null;
  let dozen: DozenNumber | null = null;
  let column: ColumnNumber | null = null;

  if (!isZero && value !== null) {
    parity = value % 2 === 0 ? 'even' : 'odd';
    highLow = value <= 18 ? 'low' : 'high';
    dozen = (Math.ceil(value / 12) as DozenNumber);
    // Column 1 holds 1,4,7…34; column 2 holds 2,5,8…35; column 3 holds 3,6,9…36.
    column = (((value - 1) % 3) + 1) as ColumnNumber;
  }

  return { id, value, colour: colourOf(id), parity, highLow, dozen, column, ringIndex, isZero };
}

function buildWheel(
  style: WheelStyle,
  label: string,
  sequence: readonly PocketId[],
  houseEdge: number,
): WheelDefinition {
  const ordered = sequence.map(buildPocket);
  const pockets = new Map<PocketId, Pocket>(ordered.map((p) => [p.id, p]));
  return {
    style,
    label,
    pocketCount: sequence.length,
    sequence,
    pockets,
    ordered,
    houseEdge,
  };
}

export const SINGLE_ZERO_WHEEL: WheelDefinition = buildWheel(
  'single-zero',
  'Single zero',
  SINGLE_ZERO_SEQUENCE,
  -1 / 37,
);

export const DOUBLE_ZERO_WHEEL: WheelDefinition = buildWheel(
  'double-zero',
  'Double zero',
  DOUBLE_ZERO_SEQUENCE,
  -2 / 38,
);

export const WHEELS: Readonly<Record<WheelStyle, WheelDefinition>> = {
  'single-zero': SINGLE_ZERO_WHEEL,
  'double-zero': DOUBLE_ZERO_WHEEL,
};

export const WHEEL_STYLES: readonly WheelStyle[] = ['single-zero', 'double-zero'];

export function getWheel(style: WheelStyle): WheelDefinition {
  return WHEELS[style];
}

/** Pockets present on a given wheel — `'00'` exists on the double-zero wheel only. */
export function hasPocket(wheel: WheelDefinition, id: PocketId): boolean {
  return wheel.pockets.has(id);
}

export function pocketAt(wheel: WheelDefinition, ringIndex: number): Pocket {
  const n = wheel.pocketCount;
  const wrapped = ((ringIndex % n) + n) % n;
  const pocket = wheel.ordered[wrapped];
  if (!pocket) throw new Error(`No pocket at ring index ${ringIndex}`);
  return pocket;
}

export function ringIndexOf(wheel: WheelDefinition, id: PocketId): number {
  const pocket = wheel.pockets.get(id);
  if (!pocket) throw new Error(`Pocket '${id}' is not on the ${wheel.style} wheel`);
  return pocket.ringIndex;
}

/**
 * `n` pockets either side of `id` on the physical ring, plus `id` itself,
 * returned in clockwise ring order. Wraps around.
 */
export function neighbours(wheel: WheelDefinition, id: PocketId, n: number): PocketId[] {
  if (n < 0) throw new Error('Neighbour count must be non-negative');
  const centre = ringIndexOf(wheel, id);
  const span = Math.min(n, Math.floor((wheel.pocketCount - 1) / 2));
  const out: PocketId[] = [];
  for (let offset = -span; offset <= span; offset += 1) {
    out.push(pocketAt(wheel, centre + offset).id);
  }
  return out;
}

/**
 * The clockwise arc from `from` to `to`, inclusive of both ends. Used to derive
 * French call-bet sectors from the wheel sequence rather than hardcoding them.
 */
export function arc(wheel: WheelDefinition, from: PocketId, to: PocketId): PocketId[] {
  const start = ringIndexOf(wheel, from);
  const end = ringIndexOf(wheel, to);
  const length = ((end - start + wheel.pocketCount) % wheel.pocketCount) + 1;
  const out: PocketId[] = [];
  for (let i = 0; i < length; i += 1) out.push(pocketAt(wheel, start + i).id);
  return out;
}

/** Shortest distance between two pockets around the ring, in pockets. */
export function ringDistance(wheel: WheelDefinition, a: PocketId, b: PocketId): number {
  const forward =
    (ringIndexOf(wheel, b) - ringIndexOf(wheel, a) + wheel.pocketCount) % wheel.pocketCount;
  return Math.min(forward, wheel.pocketCount - forward);
}
